import { DesktopOutlined, MobileOutlined, TabletOutlined } from '@ant-design/icons';
import { Segmented } from 'antd';
import { createElement, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** The three preview form-factors offered by the device switcher. */
export type DeviceKind = 'desktop' | 'tablet' | 'mobile';

type DevicePreset = {
  /** Max rendered width of the form, in px. `null` = fill available space. */
  maxWidth: number | null;
  /** i18n key under `preview.device.*` for the Segmented label. */
  labelKey: string;
  /** Icon shown alongside the label in the Segmented control. */
  icon: ComponentType;
};

/**
 * Single source of truth for the device switcher — drives both the frame's
 * width cap (below) and the Segmented options in <PreviewPage>. Tablet/mobile
 * widths mirror common iPad/iPhone logical widths; the mobile cap sits below
 * <FormShell>'s RAIL_COLLAPSE_WIDTH so the step rail collapses at that size.
 */
export const DEVICE_PRESETS: Record<DeviceKind, DevicePreset> = {
  desktop: { maxWidth: null, labelKey: 'preview.device.desktop', icon: DesktopOutlined },
  tablet: { maxWidth: 834, labelKey: 'preview.device.tablet', icon: TabletOutlined },
  mobile: { maxWidth: 390, labelKey: 'preview.device.mobile', icon: MobileOutlined },
};

/** Order used to render the Segmented control. */
export const DEVICE_ORDER: DeviceKind[] = ['desktop', 'tablet', 'mobile'];

/**
 * Shared Desktop / Tablet / Mobile viewport toggle used by both the standalone
 * <PreviewPage> and the approver's <InlineWorkflowPreviewModal>. Extracted so
 * the two surfaces stay visually identical when either gets a polish.
 *
 * Design: icon stacked above the label at default Segmented size — the
 * previous inline-icon + `size="small"` combination collapsed the tablet /
 * mobile glyphs into near-invisible strokes. Stacking gives each option a
 * clear device silhouette and the trio reads as a proper "form-factor
 * switcher" instead of three cramped chips.
 */
export function DeviceViewportSwitcher({
  device,
  onChange,
}: {
  device: DeviceKind;
  onChange: (kind: DeviceKind) => void;
}) {
  const { t } = useTranslation();
  return (
    <Segmented<DeviceKind>
      value={device}
      onChange={onChange}
      aria-label={t('preview.device.label')}
      options={DEVICE_ORDER.map((kind) => ({
        value: kind,
        label: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              padding: '4px 6px',
              minWidth: 56,
              lineHeight: 1.2,
            }}
          >
            <span style={{ fontSize: 18, display: 'inline-flex' }}>
              {createElement(DEVICE_PRESETS[kind].icon)}
            </span>
            <span style={{ fontSize: 12, fontWeight: 500 }}>
              {t(DEVICE_PRESETS[kind].labelKey)}
            </span>
          </div>
        ),
      }))}
    />
  );
}

/**
 * Centers and width-caps its children to emulate a desktop / tablet / mobile
 * screen. Purely presentational — the actual responsive reflow (collapsing the
 * step rail) is owned by <FormShell>, which measures its own rendered width.
 * That keeps the "resize the div and it reflows" contract: this frame only has
 * to change the width.
 */
export function DevicePreviewFrame({
  device,
  children,
}: {
  device: DeviceKind;
  children: ReactNode;
}) {
  const preset = DEVICE_PRESETS[device];
  const framed = preset.maxWidth !== null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', flex: 1, minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          width: '100%',
          maxWidth: preset.maxWidth ?? undefined,
          transition: 'max-width 200ms ease',
          // Non-desktop presets get subtle "screen" chrome so the constrained
          // width reads as an intentional device frame, not a squished page.
          ...(framed
            ? {
                border: '1px solid #e5e7eb',
                borderRadius: 16,
                boxShadow: '0 8px 30px rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
                background: '#fff',
              }
            : null),
        }}
      >
        {children}
      </div>
    </div>
  );
}
